//------------------------------------------------------------------------
import styled from "styled-components";
import {alpha, Box, Grid, IconButton, Tooltip, Typography} from "@mui/material";
import React, {ReactElement, ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ToolbarNameValueData, {ToolbarEntry} from "../../ToolbarNameValueData";
import {formatTimestamp} from "../../../utils/datetime";
import {BaseModelType} from "../../../app/api/types";
import {usePermission} from "../../../hooks/usePermission";
import {PermissionLevel} from "../../../app/api/model/user";

const TitleBar = styled(Box)`
    padding: 15px;
    margin: 0 0 15px 0;
    display: flex;
    align-items: center;
`;

export const ContentHeader = styled(Box)`
    padding: 15px;
`;

export interface TitleBarButton {
    key: string;
    disabled: boolean;
    minRequiredPermissionToSee: PermissionLevel;
    tooltip: string;
    tooltipDisabled?: string;
    icon: ReactNode;
    callback: () => void;
}

type ToolbarValue<T> = {
    translationKey: string;
    fieldName: keyof T;
    render?: (value?: any) => ReactNode;
};

type ExtendedModelType = BaseModelType & {
    id: number;
    createdAt?: string;
    updatedAt?: string;
};

interface ContentTitleProps<T extends ExtendedModelType> {
    titleKey: string;
    subtitle?: string | number;
    buttons?: TitleBarButton[];
    applySearchParams?: boolean;
    children?: ReactElement;
    toolEntries?: ToolbarValue<T>[];
    entry?: T;
}

export default function ContentTitle<T extends ExtendedModelType>({
    titleKey,
    subtitle,
    buttons = [],
    children,
    toolEntries = [],
    entry,
}: Readonly<ContentTitleProps<T>>) {
    const {t} = useTranslation();
    const toolBarNameValues: ToolbarEntry[] = toolEntries.map((toolBarValue: ToolbarValue<T>) => {
        const value = toolBarValue.fieldName && entry ? entry[toolBarValue.fieldName] : undefined;
        return {
            name: t(toolBarValue.translationKey),
            value: toolBarValue.render ? toolBarValue.render(value) : value,
        };
    });
    const {hasPermission} = usePermission();

    const renderButtons = () =>
        buttons
            .filter((button) => hasPermission(button.minRequiredPermissionToSee))
            .map((button) => {
                const tooltip = button.disabled ? button.tooltipDisabled ?? "" : button.tooltip;
                return (
                    <Tooltip title={t(tooltip)} key={`button.tooltip${button.key}`}>
                        <span>
                            <IconButton disabled={button.disabled} onClick={button.callback}>
                                {button.icon}
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            });
    const navigate = useNavigate();
    const haveHistory = window.history.length > 1;
    return (
        <ContentHeader>
            <TitleBar
                sx={{
                    pl: {sm: 2},
                    pr: {xs: 1, sm: 1},
                    ...{
                        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                    },
                }}
            >
                <Tooltip title={t("generic_backToList")} key={"back"}>
                    <IconButton disabled={!haveHistory} onClick={() => navigate(-1)}>
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>
                <Box>
                    <Typography variant={"h2"}>{t(titleKey)}</Typography>
                    {subtitle && (
                        <Typography variant="subtitle1" fontSize={14}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {entry && (
                    <ToolbarNameValueData
                        data={[
                            {name: t("generic_id"), value: `${entry.id}`},
                            ...toolBarNameValues,
                            {name: t("generic_createdAt"), value: formatTimestamp(entry.createdAt)},
                            {name: t("generic_updatedAt"), value: formatTimestamp(entry.updatedAt)},
                        ]}
                    />
                )}
                {renderButtons()}
            </TitleBar>
            <Grid container columns={10}>
                {children}
            </Grid>
        </ContentHeader>
    );
}
